import { history } from 'umi';
import { makeObservable, action, observable, runInAction } from 'mobx';
import bridge from '@/utils/v2/bridge';
import { useFormatMsg } from '@/utils/locale';
import { Toast } from 'antd-mobile';
import { BaseService } from '../base';
import Repayment from './api.interface';
import { RepaymentModel } from './repayment.model';
import {
  REFACTOR_REPAY_STATUS,
  CodeHandlers,
  RepaymentMethod,
  RepaymentParams,
  IRepayOrder,
} from './repayment.interface';
import insight from '@/utils/v2/insight';
import { sendUserAction, UserActions } from '@/utils/v2/idap';
import { cashier } from '@shopeepay/payment-sdk-web';

const formatMessage = useFormatMsg();

export class CommonService extends BaseService {
  @observable repayment: RepaymentModel = {
    total_amount: 0,
    overflow_amount: 0,
    overdueInfo: {} as IRepayOrder,
    repayableInfo: {} as IRepayOrder,
    selectedChannelName: '',
    selectedChannelId: 0,
    payOption: '',
    loading: false,
  } as RepaymentModel;

  @observable isSpmCashierAvailable = false;

  constructor() {
    super();
    makeObservable(this);
  }

  @action
  async initRepayInfo() {
    // 初始化
    this.repayment = {
      ...this.repayment,
      total_amount: 0,
      overflow_amount: 0,
      overdueInfo: {} as IRepayOrder,
      repayableInfo: {} as IRepayOrder,
      selectedChannelName: '',
      selectedChannelId: 0,
      payOption: '',
      loading: false,
    };
    this.isSpmCashierAvailable = false;
  }

  @action
  clearSelectedChannel() {
    runInAction(() => {
      this.repayment = {
        ...this.repayment,
        payOption: '',
        selectedChannelName: '',
        selectedChannelId: 0,
      };
    });
  }

  /**
   * 启动独立收银台
   */
  @action
  async launchCashier(orderSn: string) {
    insight.reportThirdSDKCounter({
      sdkName: 'spm_cashier_launch',
      method: 'call',
    });
    sendUserAction(UserActions.Repayment.CallSpmCashierLaunch, {
      orderSn,
    });

    const resp = await cashier.launch(orderSn, {
      from: 'fastescrow', // for SPM side analysis
    });

    sendUserAction(UserActions.Repayment.CallSpmCashierLaunchResponse, {
      resp,
    });

    console.log('cashier.launch resp ------>');
    console.log(resp);
  }

  /**
   * 独立收银台相关的通用处理逻辑
   */
  @action
  async handleSpmCashier(order_sn: string) {
    this.repayment.loading = false;

    // 启动cashier并等待用户返回
    await new Promise((resolve) => {
      let isResolved = false;

      const reappearCb = () => {
        // 确保回调只执行一次
        if (isResolved) {
          return;
        }
        isResolved = true;

        // 解绑事件监听器
        bridge.unBind('viewWillReappear', reappearCb);
        // 重新获取还款信息，刷新页面数据
        this.getRepayInfo({ isForHome: false }).then(() => {
          resolve(true);
        });
      };

      // 绑定页面重新出现事件
      bridge.bind('viewWillReappear', reappearCb);
      // 启动cashier
      this.launchCashier(order_sn);
    });
  }

  /**
   * 独立收银台终态处理逻辑
   */
  @action
  handleFinalState(proactive_repay_id: string) {
    this.repayment.loading = false;
    history.push({
      pathname: `/billing/repay-result/${proactive_repay_id ?? ''}`,
    });
  }

  /**
   * 独立收银台默认处理逻辑
   */
  @action
  handleDefault() {
    this.repayment.loading = false;
  }

  /**
   * 独立收银台错误处理逻辑
   */
  @action
  handleError(message?: string) {
    this.repayment.loading = false;
    Toast.show({
      icon: 'fail',
      content: message || formatMessage({ id: 'system_busy' }),
    });
  }

  /**
   * 独立收银台的 triggerRepayBySpmCashier 通用实现
   * 子类可以重写 createSpmCashierCodeHandlers 方法来实现国家特定的逻辑
   */
  @action
  async triggerRepayBySpmCashier({
    overflow_amount,
    list,
    is_icp_repay = RepaymentMethod.SPM_CASHIER,
  }: RepaymentParams) {
    this.repayment.loading = true;

    const params = {
      overflow_amount,
      list,
      is_icp_repay,
    };

    // 发送埋点
    sendUserAction(UserActions.Repayment.CallTriggerRepayment, {
      params,
    });

    // 调用API
    const response = await this.post(Repayment.API.proactive_repay, {
      body: params,
      isUseDefaultErrorMsg: true,
    });

    // 发送响应埋点
    sendUserAction(UserActions.Repayment.CallTriggerRepaymentResponse, {
      resp: response,
    });

    const { code, result } = response;

    // 创建 CodeHandlers，子类可以重写此方法
    const codeHandlers: CodeHandlers = this.createSpmCashierCodeHandlers(result);

    // 执行对应的处理器
    const handler = codeHandlers[code] || codeHandlers.default;
    handler();
  }

  /**
   * 创建独立收银台的 CodeHandlers
   * 子类可以重写此方法来实现国家特定的处理逻辑
   */
  @action
  createSpmCashierCodeHandlers(result: any): CodeHandlers {
    const { proactive_repay_id, repay_status, order_sn } = result || {};

    const isFinalState = [
      REFACTOR_REPAY_STATUS.CANCELLED,
      REFACTOR_REPAY_STATUS.FAILED,
      REFACTOR_REPAY_STATUS.DEDUCTED,
      REFACTOR_REPAY_STATUS.COMPLETE,
    ].includes(repay_status);

    const isSpmCashier = Boolean(this.isSpmCashierAvailable && order_sn);

    return {
      0: async () => {
        switch (true) {
          case isFinalState:
            // 与BE沟通确认以下这几种状态直接到终态（溢缴款全量抵扣）,直接跳转还款结果页
            this.handleFinalState(proactive_repay_id);
            break;
          case isSpmCashier:
            // 收银台处理
            await this.handleSpmCashier(order_sn);
            break;
          default:
            // 默认错误处理
            this.handleError();
            break;
        }
      },
      default: () => this.handleDefault(),
    };
  }

  /**
   * 获取还款信息 - 各国家实现不同，这里只是占位方法
   * 子类应该重写此方法以实现各自的逻辑
   */
  @action
  async getRepayInfo({ isForHome }: { isForHome: boolean }): Promise<any> {
    // 默认占位实现，子类应该重写此方法
    console.log(isForHome);
    console.warn('getRepayInfo method should be implemented by subclass');
    return true;
  }

  @action
  async getRepayResult(data: { repay_id: string }) {
    const result = await this.post(Repayment.API.get_repay_result, {
      body: data,
      isUseDefaultErrorMsg: true,
    });
    return result;
  }
}
